import { z } from "zod";

import {
  resolveWhatsappMessage,
  whatsappChatUrl,
  type WhatsappMessageSubject,
} from "../apps/whatsapp-widget";
import {
  tourPageDepartureDetailSchema,
  tourPageTrustBadgeSchema,
  type TourPageSettings,
} from "./settings";

/**
 * The number and greeting a departure affordance opens WhatsApp with.
 *
 * The message still holds its placeholders: they resolve per departure row,
 * because the date differs on every row and this payload is cached once per
 * agency.
 */
const whatsappChatSchema = z.object({
  number: z.string(),
  message: z.string(),
});

/** The icon button beside Book now. Nothing to configure beyond the chat
 *  itself, so this is the chat. */
const whatsappButtonViewSchema = whatsappChatSchema;

/** The "Ask on WhatsApp" line in the expanded details panel, which also
 *  carries the operator's own wording for the two bits of text around it. */
const whatsappContactViewSchema = whatsappChatSchema.extend({
  promptLabel: z.string(),
  linkLabel: z.string(),
});

export type TourPageWhatsappView = z.infer<typeof whatsappChatSchema>;
export type TourPageWhatsappContactView = z.infer<
  typeof whatsappContactViewSchema
>;

/**
 * What the storefront receives. Everything that should not render is ABSENT
 * rather than empty, so the storefront has one thing to check per surface
 * instead of re-deriving the rules.
 */
export const tourPageViewSchema = z.object({
  recommendedOn: z.string().min(1).optional(),
  trustBadges: z.array(tourPageTrustBadgeSchema).min(1).optional(),
  award: z
    .object({
      imageUrl: z.string(),
      title: z.string().optional(),
      tagline: z.string().optional(),
    })
    .optional(),
  departures: z.object({
    details: z.array(tourPageDepartureDetailSchema).min(1).optional(),
    whatsappButton: whatsappButtonViewSchema.optional(),
    whatsappContact: whatsappContactViewSchema.optional(),
  }),
  askQuestionHeading: z.string().min(1).nullable(),
});
export type TourPageView = z.infer<typeof tourPageViewSchema>;

/**
 * What the traveler feed returns. `null` now means only one thing: a stored
 * settings row that FAILED to parse. An agency that has never saved any gets
 * a populated view built from the schema's defaults, because
 * `TourPageReader.getView` parses `settings ?? {}` rather than returning
 * early — this docblock said the opposite until 2026-10-26, and a reader
 * chasing a missing field was sent to the wrong conclusion by it.
 *
 * Wrapped rather than bare, matching `whatsappWidgetResponseSchema`, so the
 * storefront loader reads `data.tourPage` like every sibling loader.
 */
export const tourPageResponseSchema = z.object({
  tourPage: tourPageViewSchema.nullable(),
});
export type TourPageResponse = z.infer<typeof tourPageResponseSchema>;

type TourPageAwardView = NonNullable<TourPageView["award"]>;

/**
 * The award badge, or nothing.
 *
 * Needs BOTH its switch and its artwork: the seal is the point, and a title
 * with no image is not an award badge.
 */
function awardView(
  award: TourPageSettings["award"],
): TourPageAwardView | undefined {
  if (!award.enabled || !award.imageUrl) return undefined;
  return {
    imageUrl: award.imageUrl,
    ...(award.title ? { title: award.title } : {}),
    ...(award.tagline ? { tagline: award.tagline } : {}),
  };
}

/**
 * The chat both departure affordances open, or nothing.
 *
 * A blank number is the whole gate: an operator can switch a surface on before
 * they have a number to put in it, and a `wa.me` link addressed to nothing is
 * worse than no link. Stated once here so the two surfaces below cannot answer
 * it differently.
 */
function whatsappChat(
  departures: TourPageSettings["departures"],
): TourPageWhatsappView | undefined {
  if (!departures.whatsappNumber) return undefined;
  return {
    number: departures.whatsappNumber,
    message: departures.whatsappMessage,
  };
}

/**
 * Settings to what actually renders.
 *
 * The single place the "should this appear?" rules live — here and in the
 * helpers above — so the operator preview, traveler-api and the storefront
 * cannot disagree.
 *
 * TWO WhatsApp surfaces, each absent-or-complete. They were collapsed into one
 * `whatsapp` key while the number was borrowed from the WhatsApp Widget app and
 * only the details link was built; both halves of that changed on 2026-09-17.
 * Each surface carries its own copy of the number and message — ~60 bytes of a
 * once-per-agency payload — so a caller has exactly one thing to check, which
 * is this type's own rule.
 */
export function buildTourPageView(settings: TourPageSettings): TourPageView {
  const { recommendedOn, trustBadges, departures } = settings;

  const details = departures.detailsEnabled ? departures.details : [];
  const award = awardView(settings.award);
  const chat = whatsappChat(departures);

  const button = chat && departures.whatsappButtonEnabled ? chat : undefined;

  // `detailsEnabled` AND the contact switch. The contact link sits at the foot
  // of the details panel, and the operator screen nests its switch inside
  // "Show additional details" — so gating on the inner switch alone STRANDED
  // it: turning the section off hid the control while the link kept rendering,
  // leaving a live WhatsApp link the operator could neither see nor remove.
  //
  // Gated rather than cleared on save, so an operator who switches the section
  // back on finds their wording and their choice where they left them.
  const contact =
    chat && departures.detailsEnabled && departures.whatsappContactEnabled
      ? {
          ...chat,
          promptLabel: departures.whatsappPromptLabel,
          linkLabel: departures.whatsappLinkLabel,
        }
      : undefined;

  return {
    ...(recommendedOn ? { recommendedOn } : {}),
    ...(trustBadges.length > 0 ? { trustBadges } : {}),
    ...(award ? { award } : {}),
    departures: {
      ...(details.length > 0 ? { details } : {}),
      ...(button ? { whatsappButton: button } : {}),
      ...(contact ? { whatsappContact: contact } : {}),
    },
    askQuestionHeading: settings.askQuestionHeading,
  };
}

/**
 * The `wa.me` link for one departure row — for either surface, since both
 * carry the same `{ number, message }`.
 *
 * The NUMBER is this feature's own, but the placeholder resolver and the link
 * builder still come from the WhatsApp Widget app: address formatting and token
 * substitution are one implementation shared by both features, and duplicating
 * them is how two WhatsApp links start escaping their text differently.
 */
export function tourPageDepartureChatUrl(
  whatsapp: TourPageWhatsappView,
  subject: WhatsappMessageSubject,
): string {
  return whatsappChatUrl(
    whatsapp.number,
    resolveWhatsappMessage(whatsapp.message, subject),
  );
}
